import { measurementMath } from '../shared/measurementMath'
import { convertNumber } from './conversions'
import { GeckoMeasurementUnit } from './geckoMeasurementUnit'

const { round } = measurementMath

// -----------------------------------------------------------------------------

export type GeckoMeasurementJson = {
  num: number
  unit: string
}

/**
 * A frequently-used helper class which wraps a number and a measurement unit,
 * ensuring that everything from position measurement to thickness readings
 * carry their respective measurement unit along with them through the UI.
 */
export class GeckoMeasurement {
  //----------------------------------------------------------------------------

  /** The number this class wraps */
  public readonly num: number

  /** The measurement unit this value carries */
  public readonly unit: GeckoMeasurementUnit

  /**
   * A simple constructor- private to encourage use of more descriptive static
   * factory methods like CreateFromValue()
   * @param num               The numeric value being wrapped
   * @param unit              The measurement unit of this value
   * @private
   */
  private constructor(num: number, unit: GeckoMeasurementUnit) {
    if (num === null)
      throw new Error('GeckoMeasurement created with null number!')
    if (num === undefined)
      throw new Error('GeckoMeasurement created with undefined number!')
    if (unit === null)
      throw new Error('GeckoMeasurement created with null unit!')
    if (unit === undefined)
      throw new Error('GeckoMeasurement created with undefined unit!')
    this.num = num
    this.unit = unit
  }

  //----------------------------------------------------------------------------

  /**
   * A method for creating a GeckoMeasurement from a JSON object
   */
  public static CreateFromJSON(json: GeckoMeasurementJson): GeckoMeasurement {
    return GeckoMeasurement.CreateFromValue(
      json.num,
      GeckoMeasurementUnit.GetOrCreate(json.unit)
    )
  }

  /**
   * Factory method for creating a measurement from a number and measurement unit
   * @param num               The numeric value being wrapped
   * @param unit              The measurement unit of this value
   * @constructor
   */
  public static CreateFromValue(num: number, unit: GeckoMeasurementUnit) {
    return new GeckoMeasurement(num, unit)
  }

  /**
   * A method for checking whether two measurements are exactly equal, not
   * just equivalent (i.e. 0 in !== 0 ft)
   * @param a               The first measurement object, or undefined
   * @param b               The second measurement object, or undefined
   * @constructor
   */
  public static Equals(
    a: GeckoMeasurement | undefined,
    b: GeckoMeasurement | undefined
  ): boolean {
    if (a === b) return true
    if (!a) return !b
    if (!b) return !a
    return a.unit === b.unit && a.num === b.num
  }

  public static Equivalent(
    a: GeckoMeasurement | undefined,
    b: GeckoMeasurement | undefined
  ): boolean {
    if (a === b) return true
    if (!a) return !b
    if (!b) return !a
    return GeckoMeasurement.Equals(a, b.convertTo(a.unit))
  }

  public static Min(...measurements: GeckoMeasurement[]) {
    return measurements.reduce((min, curr) => (curr.lte(min) ? curr : min))
  }

  public static Max(...measurements: GeckoMeasurement[]) {
    return measurements.reduce((min, curr) => (curr.gte(min) ? curr : min))
  }

  /**
   * Create a GeckoMeasurement with a value of Infinity
   * @param unit unit for new GeckoMeasurement. Defaults to GeckoMeasurementUnit.None
   * @returns
   */
  public static Inf(unit: GeckoMeasurementUnit = GeckoMeasurementUnit.None) {
    return new GeckoMeasurement(Infinity, unit)
  }

  /**
   * Create a GeckoMeasurement with a value of -Infinity
   * @param unit unit for new GeckoMeasurement. GeckoMeasurementUnit.None
   * @returns
   */
  public static NegInf(unit: GeckoMeasurementUnit = GeckoMeasurementUnit.None) {
    return new GeckoMeasurement(-Infinity, unit)
  }

  /**
   * Create a GeckoMeasurement with a value of 0
   * @param unit unit for new GeckoMeasurement. GeckoMeasurementUnit.None
   * @returns
   */
  public static Zero(unit: GeckoMeasurementUnit = GeckoMeasurementUnit.None) {
    return new GeckoMeasurement(0, unit)
  }

  //----------------------------------------------------------------------------

  /**
   * Returns a copy of this measurement converted to some new measurement unit.
   * If this measurement is in Tubes or Degrees, return it as-is.
   *
   * NOTE: By default the conversion returned by this method is rounded to 10
   *       significant figures, this helps to avoid floating point errors, but
   *       also means that large conversions (i.e. meters to millimeters) could
   *       be clipped.
   *
   * @param newUnit           The new measurement unit to adopt
   */
  public convertTo(
    newUnit: GeckoMeasurementUnit | undefined
  ): GeckoMeasurement {
    if (this.unit === newUnit || !newUnit) return this
    // Don't convert tubes
    if (this.unit === GeckoMeasurementUnit.Tubes) return this

    if (newUnit === GeckoMeasurementUnit.Frames) {
      throw new Error(
        "Convert to frames using convertToFrames instead, using the asset model's frame size"
      )
    }

    const convertedValue = convertNumber(this.num, this.unit, newUnit)

    return GeckoMeasurement.CreateFromValue(
      Number(convertedValue.toPrecision(10)),
      newUnit
    )
  }

  public convertFromTubes(tubeSpacing: GeckoMeasurement): GeckoMeasurement {
    return GeckoMeasurement.CreateFromValue(
      this.num * tubeSpacing.num,
      tubeSpacing.unit
    )
  }

  public convertToFrames(frameSize: GeckoMeasurement): GeckoMeasurement {
    return GeckoMeasurement.CreateFromValue(
      this.convertTo(frameSize.unit).num / frameSize.num,
      GeckoMeasurementUnit.Frames
    )
  }

  // TODO: This will replace convertTo() entirely
  public toNumber(unit: GeckoMeasurementUnit): number {
    return this.convertTo(unit).num
  }

  /**
   * Returns the sum of two measurements, converting to the first's units
   * @param other           The other measurement to add to this one, or
   *                        undefined (which does nothing)
   */
  public plus(other: GeckoMeasurement | undefined): GeckoMeasurement {
    return !other || other.num === 0
      ? this
      : new GeckoMeasurement(this.num + other.toNumber(this.unit), this.unit)
  }

  /**
   * Returns the difference between two measurements, converting to the first's units
   * @param other           The other measurement to subtract from this one,
   *                        or undefined (which does nothing)
   */
  public minus(other: GeckoMeasurement | undefined): GeckoMeasurement {
    return !other || other.num === 0
      ? this
      : new GeckoMeasurement(
          this.num - other.convertTo(this.unit).num,
          this.unit
        )
  }

  /**
   * Scales a measurement by a set factor (allows multiplication and division)
   * @param factor          The factor to scale this by
   */
  public scale(factor: number): GeckoMeasurement {
    if (factor === 1) return this
    return new GeckoMeasurement(this.num * factor, this.unit)
  }

  /**
   * Returns the absolute value of this measurement
   */
  public abs(): GeckoMeasurement {
    return this.num >= 0 ? this : this.neg()
  }

  /**
   * Returns the negated value of this measurement
   */
  public neg(): GeckoMeasurement {
    return new GeckoMeasurement(-this.num, this.unit)
  }

  /**
   * Returns this measurement rounded to a set precision (in decimal places,
   * 0 by default)
   * @param precision       The number of decimal places to round to
   */
  public round(precision?: number): GeckoMeasurement {
    return new GeckoMeasurement(round(this.num, precision ?? 0), this.unit)
  }

  /**
   * Returns this measurement rounded to a set precision (in significant figures,
   * 0 by default)
   * @param precision       The number of significant figures to round to
   */
  public toPrecision(precision?: number): GeckoMeasurement {
    return new GeckoMeasurement(
      Number(this.num.toPrecision(precision ?? 0)),
      this.unit
    )
  }

  /**
   * Returns if this measurement is greater-than the passed in measurement
   * @param measurement - the measurement to compare to `this`
   */
  public gt(measurement: GeckoMeasurement): boolean {
    const measurementToCompare = measurement.convertTo(this.unit)
    return this.num > measurementToCompare.num
  }

  /**
   * Returns if this measurement is greater-than or equal to the passed in measurement
   * @param measurement - the measurement to compare to `this`
   */
  public gte(measurement: GeckoMeasurement): boolean {
    const measurementToCompare = measurement.convertTo(this.unit)
    return this.num >= measurementToCompare.num
  }

  /**
   * Returns if this measurement is less-than the passed in measurement
   * @param measurement - the measurement to compare to `this`
   */
  public lt(measurement: GeckoMeasurement): boolean {
    const measurementToCompare = measurement.convertTo(this.unit)
    return this.num < measurementToCompare.num
  }

  /**
   * Returns if this measurement is less-than or equal to the passed in measurement
   * @param measurement - the measurement to compare to `this`
   */
  public lte(measurement: GeckoMeasurement): boolean {
    const measurementToCompare = measurement.convertTo(this.unit)
    return this.num <= measurementToCompare.num
  }

  /**
   * A method returning a legible string form of this object's data
   */
  public toString(): string {
    return `${this.num} ${this.unit.id}`
  }

  /**
   * A method returning a JSON-serializable form of this object
   */
  public toJSON(): GeckoMeasurementJson {
    return { num: this.num, unit: this.unit.id }
  }
}
